import { useEffect, useRef, useState } from "react";
import { getSubFilterBykeyFilter } from "../../auth/apiCategory";
import { filterIdOf } from "../../dropshippingInterne/hooks/useLinkedCategories";
import { toDefs } from "../../dropshippingInterne/provider/ProviderFiltersEditor";
import { elakayAutoText } from "../../pageInside/dataElakay";

const cleanKey = (value) => String(value).toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9_]/g, "");
const hasValue = (value) => value !== "" && value !== undefined && value !== null;
const unique = (values) => [...new Set(values)];
const normalizeFilter = (filter) => typeof filter?._id === "object"
  ? filter
  : { _id: filter, Filters: [] };
const edgeId = (filterId, value) => JSON.stringify([filterId, value]);

// Only the working rows for the current category belong here. The hook never
// rewrites those rows or filtersCategory: cascade discovery must not hydrate
// the editor again and erase a quantity, price or filter edit.
export default function useSellerFilterDefinitions({
  filtersCategory = [], rows = [], categoryId, productId, infoSource, onError,
}) {
  const scope = JSON.stringify([String(productId ?? ""), String(categoryId ?? "")]);
  const [selections, setSelections] = useState({});
  const [batches, setBatches] = useState({});
  const selectedByKey = selections[scope] || {};
  const scopeBatches = batches[scope] || {};
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const currentScope = useRef({ key: scope, revision: 0 });
  if (currentScope.current.key !== scope) {
    currentScope.current = { key: scope, revision: currentScope.current.revision + 1 };
  }
  const mounted = useRef(false);
  const pending = useRef(new Map());
  useEffect(() => {
    mounted.current = true;
    const pendingRequests = pending.current;
    return () => {
      mounted.current = false;
      currentScope.current.revision += 1;
      pendingRequests.clear();
    };
  }, []);

  const usedByKey = {};
  rows.forEach((row) => Object.entries(row.filter || {}).forEach(([key, value]) => {
    if (hasValue(value)) usedByKey[key] = unique([...(usedByKey[key] || []), String(value)]);
  }));
  const usedSignature = JSON.stringify(usedByKey);
  // Seed immediately in the derived view as well as storing the selection, so
  // loading rows later, or replacing a historical definition after a cascade
  // response, never produces a temporarily empty select.
  const chosen = (key) => unique([...(selectedByKey[key] || []), ...(usedByKey[key] || [])]);
  useEffect(() => {
    const used = JSON.parse(usedSignature);
    setSelections((previous) => {
      const before = previous[scope] || {};
      const next = { ...before };
      let changed = false;
      Object.entries(used).forEach(([key, values]) => {
        const combined = unique([...(before[key] || []), ...values]);
        if (combined.length !== (before[key] || []).length) {
          next[key] = combined;
          changed = true;
        }
      });
      return changed ? { ...previous, [scope]: next } : previous;
    });
  }, [scope, usedSignature]);

  const definitions = (filters) => toDefs(
    filters.map(normalizeFilter).filter((filter) => filter?._id?.name && filterIdOf(filter)),
    infoSource,
    filterIdOf,
  );
  const filterDefs = [];
  const seen = new Set();
  const addDefinitions = (defs) => defs.forEach((def) => {
    if (!seen.has(def.filterId)) {
      seen.add(def.filterId);
      filterDefs.push({ ...def, values: [...def.values] });
    }
  });
  addDefinitions(definitions(filtersCategory));
  // A used child remains editable even if its parent value has been changed
  // in another draft row. Unused children are only visible while reachable.
  Object.values(scopeBatches).forEach((batch) => {
    addDefinitions(definitions(batch).filter((def) => usedByKey[def.key]?.length));
  });
  const requests = [];
  for (let index = 0; index < filterDefs.length; index += 1) {
    const def = filterDefs[index];
    chosen(def.key).forEach((value) => {
      const config = (def.subFilter || []).find((entry) =>
        String(entry.keyFilter) === value || cleanKey(entry.keyFilter) === cleanKey(value));
      if (!config) return;
      const keyFilter = String(config.keyFilter);
      const id = edgeId(def.filterId, keyFilter);
      if (Object.prototype.hasOwnProperty.call(scopeBatches, id)) {
        addDefinitions(definitions(scopeBatches[id]));
      } else if (!requests.some((request) => request.id === id)) {
        requests.push({ id, filterId: def.filterId, keyFilter });
      }
    });
  }

  // Keep the original storage keys and values for old products. These are
  // explicit saved-value options, not new catalog values or invented IDs.
  Object.keys(usedByKey).forEach((key) => {
    if (!filterDefs.some((def) => def.key === key)) {
      filterDefs.push({
        filterId: `saved-field:${key}`, key, legacy: true,
        name: elakayAutoText("h_seller_saved_filter_field", "{value0} (saved filter)", { value0: key }),
        values: [], subFilter: [],
      });
    }
  });
  filterDefs.forEach((def) => chosen(def.key).forEach((value) => {
    if (!def.values.some((option) => option.value === value)) {
      def.values.push({
        value, legacy: true,
        label: elakayAutoText("h_seller_saved_filter_value", "{value0} (saved value)", { value0: value }),
      });
    }
  }));

  const requestSignature = JSON.stringify(requests);
  useEffect(() => {
    if (!categoryId) return;
    const revision = currentScope.current.revision;
    const isCurrent = () => mounted.current && currentScope.current.key === scope && currentScope.current.revision === revision;
    JSON.parse(requestSignature).forEach(({ id, filterId, keyFilter }) => {
      const pendingKey = JSON.stringify([scope, id]);
      if (pending.current.get(pendingKey) === revision) return;
      pending.current.set(pendingKey, revision);
      Promise.resolve().then(() => getSubFilterBykeyFilter(filterId, keyFilter)).then((data) => {
        if (!isCurrent()) return;
        if (!Array.isArray(data)) throw new Error(data?.error || "Invalid filter response");
        setBatches((previous) => ({ ...previous, [scope]: { ...(previous[scope] || {}), [id]: data } }));
      }).catch(() => {
        if (isCurrent()) onErrorRef.current?.(elakayAutoText(
          "h_seller_subfilters_load_error", "Unable to load dependent filters. Select the value again to retry.",
        ));
      }).finally(() => {
        if (pending.current.get(pendingKey) === revision) pending.current.delete(pendingKey);
      });
    });
  }, [scope, categoryId, requestSignature]);

  const sellerSelectedValues = Object.fromEntries(filterDefs.map((def) => [def.filterId, chosen(def.key)]));
  const activeDefs = filterDefs.map((def) => ({
    ...def, values: def.values.filter((option) => sellerSelectedValues[def.filterId].includes(option.value)),
  })).filter((def) => def.values.length > 0);

  const updateSelection = (key, values) => {
    onErrorRef.current?.("");
    setSelections((previous) => ({ ...previous, [scope]: { ...(previous[scope] || {}), [key]: values } }));
  };
  const onToggleFilterValue = (def, value) => {
    const current = chosen(def.key);
    if (current.includes(value) && usedByKey[def.key]?.includes(value)) {
      onErrorRef.current?.(elakayAutoText(
        "h_seller_value_in_use",
        "Cannot uncheck \"{value0}\": it's still used by a filter combination below. Edit or remove that combination first.",
        { value0: value },
      ));
      return;
    }
    updateSelection(def.key, current.includes(value) ? current.filter((item) => item !== value) : [...current, value]);
  };
  const onToggleAllFilterValues = (def) => {
    const current = chosen(def.key);
    const allSelected = def.values.length > 0 && def.values.every((option) => current.includes(option.value));
    if (allSelected && usedByKey[def.key]?.length) {
      onErrorRef.current?.(elakayAutoText(
        "h_seller_values_in_use", "Cannot uncheck all: {value0} still used by a filter combination below.",
        { value0: usedByKey[def.key].join(", ") },
      ));
      return;
    }
    updateSelection(def.key, allSelected ? [] : def.values.map((option) => option.value));
  };
  return { filterDefs, activeDefs, sellerSelectedValues, onToggleFilterValue, onToggleAllFilterValues };
}
